import { type FC, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import IcTwotoneError from '~icons/ic/twotone-error'
import MdiLoading from '~icons/mdi/loading'
import { ProviderTiles } from 'src/components/Payment/PaymentDialogV2/ProviderTiles'
import { SecurePaymentNote } from 'src/components/Payment/PaymentDialogV2/SecurePaymentNote'
import { PaymentDialogContentGmoCVS } from 'src/components/Payment/providers/GmoCvs'
import { PaymentDialogContentOnerway } from 'src/components/Payment/providers/onerway'
import { PaymentDialogContentOnerwayGoogle } from 'src/components/Payment/providers/OnerwayGooglePay'
import { PaymentDialogContentOnerwayWechatPay } from 'src/components/Payment/providers/OnerwayWechatPay'
import { PaymentDialogContentPayPay } from 'src/components/Payment/providers/paypay'
import {
  type OpenPaymentDialogReturns,
  type PaymentExecuteFn,
  type PaymentOpts,
  PaymentProvider,
  type TrackingOpts,
} from 'src/services/helpers/payment'
import { createIntentToPayExecutor } from 'src/services/helpers/paymentExecutors'

interface ProviderContentProps {
  execute: PaymentExecuteFn
  trackingFnAfterFetch?: () => void
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
  onJcbAeUnionPayRequired?: () => void
}

// JCB/AE/UnionPay reuses the Onerway component but with a distinct
// `executeProvider` to pick the right backend method.
const PaymentDialogContentOnerwayJcbAeUnionPay: FC<
  ProviderContentProps
> = props => (
  <PaymentDialogContentOnerway
    {...props}
    executeProvider={PaymentProvider.onerwayJcbAeUnionPay}
  />
)

const PROVIDER_COMPONENT = {
  [PaymentProvider.onerway]: PaymentDialogContentOnerway,
  [PaymentProvider.onerwayJcbAeUnionPay]:
    PaymentDialogContentOnerwayJcbAeUnionPay,
  [PaymentProvider.onerwayGooglePay]: PaymentDialogContentOnerwayGoogle,
  [PaymentProvider.onerwayWechatPay]: PaymentDialogContentOnerwayWechatPay,
  [PaymentProvider.paypay]: PaymentDialogContentPayPay,
  [PaymentProvider.gmo]: PaymentDialogContentGmoCVS,
} as const satisfies Record<string, FC<ProviderContentProps>>

type DispatchableProvider = keyof typeof PROVIDER_COMPONENT

const isDispatchable = (
  provider: PaymentProvider | null,
): provider is DispatchableProvider =>
  provider != null && provider in PROVIDER_COMPONENT

interface PaymentFormSectionProps {
  loading: boolean
  eligibleProviders: { provider: PaymentProvider; preferred: boolean }[]
  selectedPaymentProvider: PaymentProvider | null
  setSelectedPaymentProvider: (provider: PaymentProvider) => void
  paymentOpts: PaymentOpts & TrackingOpts
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
  onJcbAeUnionPayRequired?: () => void
}

export const PaymentFormSection: FC<PaymentFormSectionProps> = ({
  loading,
  eligibleProviders,
  selectedPaymentProvider,
  setSelectedPaymentProvider,
  paymentOpts,
  onSucceeded,
  onFailed,
  onJcbAeUnionPayRequired,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <MdiLoading className="size-8 animate-spin text-primary/60" />
      </div>
    )
  }

  if (eligibleProviders.length === 0) {
    return <NoAvailableProvidersError />
  }

  return (
    <ProviderForm
      eligibleProviders={eligibleProviders}
      selectedPaymentProvider={selectedPaymentProvider}
      setSelectedPaymentProvider={setSelectedPaymentProvider}
      paymentOpts={paymentOpts}
      onSucceeded={onSucceeded}
      onFailed={onFailed}
      onJcbAeUnionPayRequired={onJcbAeUnionPayRequired}
    />
  )
}

const ProviderForm: FC<Omit<PaymentFormSectionProps, 'loading'>> = ({
  eligibleProviders,
  selectedPaymentProvider,
  setSelectedPaymentProvider,
  paymentOpts,
  onSucceeded,
  onFailed,
  onJcbAeUnionPayRequired,
}) => {
  const executeFn = useExecuteFn(paymentOpts)
  const providerWrapperRef = useRef<HTMLDivElement>(null)
  const scrollTargetRef = useRef<HTMLDivElement>(null)
  useScrollIntoViewOnExpand(
    providerWrapperRef,
    scrollTargetRef,
    selectedPaymentProvider,
  )

  const SelectedProvider = isDispatchable(selectedPaymentProvider)
    ? PROVIDER_COMPONENT[selectedPaymentProvider]
    : null

  return (
    <div className="space-y-4">
      {eligibleProviders.length > 1 && (
        <ProviderTiles
          selectedPaymentProvider={selectedPaymentProvider}
          eligibleProviders={eligibleProviders}
          onSelect={setSelectedPaymentProvider}
        />
      )}
      {selectedPaymentProvider === PaymentProvider.onerwayJcbAeUnionPay && (
        <UnionPayCreditCardOnlyNotice />
      )}
      <div ref={providerWrapperRef}>
        {SelectedProvider && selectedPaymentProvider && (
          <SelectedProvider
            key={selectedPaymentProvider}
            execute={executeFn}
            trackingFnAfterFetch={paymentOpts.trackingFnAfterFetch}
            onSucceeded={onSucceeded}
            onFailed={onFailed}
            onJcbAeUnionPayRequired={onJcbAeUnionPayRequired}
          />
        )}
      </div>
      <div ref={scrollTargetRef}>
        <SecurePaymentNote />
      </div>
    </div>
  )
}

const UnionPayCreditCardOnlyNotice: FC = () => {
  const { t } = useTranslation(['membership'])
  return (
    <div className="rounded-md bg-background-secondary px-3 py-2 text-xs leading-snug text-primary/80">
      {t('membership:payment.unionpay-credit-card-only-notice', {
        defaultValue:
          'UnionPay users: please pay with a credit card. Debit cards are not supported yet.',
      })}
    </div>
  )
}

const NoAvailableProvidersError: FC = () => {
  const { t } = useTranslation(['membership'])
  return (
    <div className="rounded-2xl bg-background-secondary px-6 py-6 text-primary">
      <IcTwotoneError className="size-10 mb-3 opacity-70" />
      <h2 className="text-base font-semibold mb-1">
        {t('membership:payment.no-available-providers.title')}
      </h2>
      <p className="text-sm opacity-80">
        {t('membership:payment.no-available-providers.description')}
      </p>
    </div>
  )
}

function useExecuteFn(opts: PaymentOpts): PaymentExecuteFn {
  return useMemo(
    () =>
      opts.executePayment ??
      createIntentToPayExecutor({
        itemId: opts.itemId,
        oneTimeMembershipId: opts.oneTimeMembership?.id,
        interval: opts.interval,
        currency: opts.currency,
        shouldDiscount: opts.shouldDiscount,
        redirect: opts.redirect,
        promotionOption: opts.promotionOption,
      }),
    [
      opts.executePayment,
      opts.itemId,
      opts.oneTimeMembership?.id,
      opts.interval,
      opts.currency,
      opts.shouldDiscount,
      opts.redirect,
      opts.promotionOption,
    ],
  )
}

/**
 * Smooth-scrolls `targetRef` into view whenever the height of `observedRef`
 * changes by more than a few pixels — used to keep the Secure Payments
 * footer visible after a provider form expands.
 */
function useScrollIntoViewOnExpand(
  observedRef: React.RefObject<HTMLElement | null>,
  targetRef: React.RefObject<HTMLElement | null>,
  resetKey: unknown,
) {
  useEffect(() => {
    const observed = observedRef.current
    const target = targetRef.current
    if (!observed || !target) return
    let lastHeight = observed.getBoundingClientRect().height
    const observer = new ResizeObserver(entries => {
      const next = entries[0]?.contentRect.height ?? lastHeight
      if (Math.abs(next - lastHeight) < 8) return
      lastHeight = next
      target.scrollIntoView({ behavior: 'smooth', block: 'end' })
    })
    observer.observe(observed)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])
}
